import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface InteractiveWindowContextValue {
  count: number;
  register: () => () => void;
}

const InteractiveWindowContext = createContext<InteractiveWindowContextValue | null>(null);

/**
 * Tracks open interactive windows (e.g. a draggable call window). While any is open, dialogs render
 * non-modal so the window behind them stays usable.
 */
export const InteractiveWindowProvider = ({ children }: { children: ReactNode }) => {
  const [count, setCount] = useState(0);

  const register = useCallback(() => {
    setCount((c) => c + 1);
    return () => setCount((c) => c - 1);
  }, []);

  const value = useMemo(() => ({ count, register }), [count, register]);

  return <InteractiveWindowContext.Provider value={value}>{children}</InteractiveWindowContext.Provider>;
};

/** Whether dialogs should be modal: `true` unless an interactive window is open (or no provider is mounted). */
export const useDialogModal = (): boolean => {
  const context = useContext(InteractiveWindowContext);
  return context ? context.count === 0 : true;
};

/** Registers the calling window as an open interactive window for as long as `isOpen` is `true`. */
export const useRegisterInteractiveWindow = (isOpen: boolean) => {
  const register = useContext(InteractiveWindowContext)?.register;

  useEffect(() => {
    if (!register || !isOpen) return;
    return register();
  }, [register, isOpen]);
};
